import { reportColors, styleReportChart } from '../../shared/report-design';
import { Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';

const valueOf = (value) => {
  const number = Number(value?.count ?? value?.total ?? value);
  return Number.isFinite(number) ? number : 0;
};
const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(valueOf(value));

export const getCampaignLeaders = (rows = [], key = 'conversions') => (
  [...rows]
    .filter((row) => valueOf(row?.[key]) > 0)
    .sort((a, b) => valueOf(b[key]) - valueOf(a[key]))
    .slice(0, 6)
);

export default function GoogleOutcomeVisuals({ data, campaigns = [], loading = false, useClicks = false, onViewCampaigns }) {
  const theme = useTheme();
  const stages = [
    { label: 'Impressions', value: valueOf(data?.impressions), color: reportColors.revenue },
    { label: 'Clicks', value: valueOf(data?.clicks), color: reportColors.categories[6] },
    { label: 'Conversions', value: valueOf(data?.conversions), color: reportColors.conversions },
  ];
  const largest = Math.max(1, ...stages.map((stage) => stage.value));
  const measure = useClicks ? 'clicks' : 'conversions';
  const leaders = getCampaignLeaders(campaigns, measure);
  const total = campaigns.reduce((sum, row) => sum + valueOf(row?.[measure]), 0);
  const chartOptions = {
    chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false }, fontFamily: theme.typography.fontFamily },
    colors: [reportColors.conversions],
    plotOptions: { bar: { horizontal: true, barHeight: '54%', borderRadius: 3 } },
    dataLabels: { enabled: false },
    grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
    xaxis: { min: 0, categories: leaders.map((row) => row.label), labels: { formatter: formatNumber } },
    yaxis: { labels: { maxWidth: 150, style: { colors: theme.palette.text.secondary, fontSize: '12px' } } },
    tooltip: { shared: false, intersect: true, y: { formatter: (value) => `${formatNumber(value)} ${measure}` } },
  };

  return (
    <Box component="section" aria-label="Google performance visualizations" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
      <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Traffic to outcomes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Compare the volume at each step on one scale.</Typography>
          {loading ? <Skeleton variant="rectangular" height={165} /> : stages[0].value > 0 ? stages.map((stage) => (
            <Box key={stage.label} sx={{ mb: 1.7 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{stage.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(stage.value)}</Typography>
              </Box>
              <Box sx={{ height: 12, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${stage.value ? Math.max(1, (stage.value / largest) * 100) : 0}%`, background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`, borderRadius: 2 }} />
              </Box>
            </Box>
          )) : <Typography variant="body2" color="text.secondary" sx={{ py: 5 }}>No traffic was returned for this range. Try another date range or adjust the filters.</Typography>}
          {!loading && stages[0].value > 0 ? <Typography variant="caption" color="text.secondary">Exact values appear above each bar.</Typography> : null}
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Campaigns driving {measure}</Typography>
              <Typography variant="body2" color="text.secondary">Top six campaigns · {formatNumber(total)} total {measure}.</Typography>
            </Box>
            {onViewCampaigns ? <Button variant="text" size="small" onClick={onViewCampaigns} sx={{ minHeight: 44, whiteSpace: 'nowrap' }}>View all</Button> : null}
          </Box>
          {loading ? <Skeleton variant="rectangular" height={260} sx={{ mt: 2 }} /> : leaders.length ? (
            <ReactApexChart options={styleReportChart(theme, chartOptions)} series={[{ name: measure, data: leaders.map((row) => valueOf(row[measure])) }]} type="bar" height={260} />
          ) : <Typography variant="body2" color="text.secondary" sx={{ py: 5 }}>No campaign {measure} were returned. Check the selected campaigns and conversion actions.</Typography>}
        </CardContent>
      </Card>
    </Box>
  );
}
